import {
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import i18next, { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { copyLang, planLimitMessage, planRef, usePlanCatalog } from '@/features/billing/catalog';
import { type ApiError, isApiError } from '@/lib/api';
import { useSession } from '@/stores/session';

/**
 * Query keys are `[tenantId, ...parts]` so switching shop gives a separate cache automatically (docs/04 §7).
 * Include `branchId` in `parts` for branch-scoped data (the X-Branch-Id header follows the session store).
 *
 *   useTenantQuery(['customers', params], () => unwrap(api.GET('/v1/customers', { params: { query: params } })))
 */
export function useTenantQuery<T>(
  parts: readonly unknown[],
  fn: () => Promise<T>,
  options?: Omit<UseQueryOptions<T, ApiError, T, QueryKey>, 'queryKey' | 'queryFn'>,
) {
  const tenantId = useSession((s) => s.tenantId);
  return useQuery<T, ApiError>({
    ...options,
    queryKey: [tenantId, ...parts],
    queryFn: fn,
    enabled: tenantId !== null && (options?.enabled ?? true),
  });
}

/** Invalidate everything under `[tenantId, ...parts]`. */
export function useInvalidate() {
  const qc = useQueryClient();
  const tenantId = useSession((s) => s.tenantId);
  return (...parts: unknown[]) => qc.invalidateQueries({ queryKey: [tenantId, ...parts] });
}

/**
 * Human message for any thrown error: a reason-specific line (`errors:reasons.<details.reason>`), a plan line that names
 * the package to move to (`errors:planLimit.<key>Next`, `errors:moduleNeeds`; names come from the plan catalog), else the server message
 * (already localised) or the i18n fallback by code.
 */
export function errorMessageOf(e: unknown, t: TFunction<'errors'>): string {
  if (isApiError(e)) {
    if (e.code === 'NETWORK') return t('NETWORK');
    const d = e.details as
      | { reason?: unknown; key?: unknown; limit?: unknown; module?: unknown; code?: unknown }
      | null
      | undefined;
    const specific: string[] = [];
    // money out (refund / keep a deposit / void) is the owner's or a manager's call: say who, not "breaks a checkout rule"
    if (e.code === 'CHECKOUT_RULE' && d?.code === 'APPROVER_REQUIRED') specific.push(t('approverRequired'));
    if (typeof d?.reason === 'string') specific.push(t(`reasons.${d.reason}`, { ...d, defaultValue: '' }));
    const catalog = usePlanCatalog.getState().catalog;
    const lang = copyLang(i18next.language);
    if (e.code === 'PLAN_LIMIT_REACHED' && typeof d?.key === 'string') {
      specific.push(
        planLimitMessage(catalog, d.key, d.limit, lang, t) || t(`planLimit.${d.key}`, { defaultValue: '' }),
      );
    }
    // the package named in these lines is the first one that has the feature, read from the catalog
    if (e.code === 'MODULE_DISABLED' && typeof d?.module === 'string') {
      specific.push(
        t(`moduleNeeds.${d.module}`, { plan: planRef(catalog, d.module, lang, t), defaultValue: '' }),
      );
    }
    if (e.code === 'PLAN_FEATURE_REQUIRED') {
      specific.push(t('PLAN_FEATURE_REQUIRED', { plan: planRef(catalog, 'checkout', lang, t) }));
    }
    const found = specific.find(Boolean);
    if (found) return found;
    return e.message && e.message !== e.code ? e.message : t(e.code, { defaultValue: t('UNKNOWN') });
  }
  return t('UNKNOWN');
}

export function useErrorMessage() {
  const { t } = useTranslation('errors');
  return (e: unknown): string => errorMessageOf(e, t);
}

/** Mutation that toasts the error by default. Pass `onError` to override. */
export function useApiMutation<TData, TVars>(options: UseMutationOptions<TData, ApiError, TVars>) {
  const msg = useErrorMessage();
  return useMutation<TData, ApiError, TVars>({
    onError: (e) => toast.error(msg(e)),
    ...options,
  });
}
